// Экран «Работы» — третий экран нового фронта (п.20, 2026-09-07).
// Две группы столбцов — «Признаки» и «Расчёт» — кнопками-переключателями
// (владелец 2026-09-09: прежние «Фильтрация · Модель · Свойства работы» с галочками
// убраны, рабочее название и единица измерения ушли в «Признаки», длительность
// и тепловой контур — в «Расчёт»).
// Все выпадающие списки тегами, предшественники тегами (пишутся
// в шаблон «Базовый»), «+ работа» строкой внизу, добавление из глобального
// справочника, удаление работы с подтверждением и разбором последствий.
// Порядок строк: как отдаёт сервер (по названию), клик по заголовку столбца
// сортирует по нему — по возрастанию, по убыванию, третий клик снимает
// сортировку и возвращает исходный порядок (замечание владельца 2026-09-08).
// Столбец «Работы факта» (2026-09-11, TZ_RABOTY_FAKT.md) — только показ тегами:
// клик открывает карточку работы на одноимённой вкладке, правка — там.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { call, p, type Data } from '../api';
import { confirmBox } from '../dialog';
import { ensureBaseTpl } from '../links';
import { Pop, at, type Anchor } from '../Pop';
import type { ShellCtx } from '../shell';
import { kontury } from '../util';
import { VyborSpiska } from '../Podskazki';

type Work = Data<'/api/projects/{pid}/works', 'get'>[number];
type Spisok = Data<'/api/projects/{pid}/spiski', 'get'>[number];
type Logika = Data<'/api/projects/{pid}/logiki', 'get'>[number];
type Project = Data<'/api/projects', 'get'>[number];
type WorkFakt = Data<'/api/projects/{pid}/works-fakt', 'get'>[number];

export const RASK_LABEL: Record<string, string> = {
  'этажи': 'по типовым этажам', 'подземные': 'по этажам ниже 0', 'технические': 'по техническим этажам',
  'квартиры': 'по этажам с квартирами', 'секции': 'по этажам и секциям',
  'корпус': 'на корпус', 'объект': 'на объект', 'вручную': 'вручную',
};
const RASK_KEY: Record<string, string> = Object.fromEntries(Object.entries(RASK_LABEL).map(([k, v]) => [v, k]));
// календарь работы (владелец 2026-09-15, #62): в группе «Расчёт», рядом с раскаткой
export const KAL_LABEL: Record<string, string> = { 'календарные': 'Календарные дни', 'без праздников': 'Без праздников' };
const KAL_KEY: Record<string, string> = Object.fromEntries(Object.entries(KAL_LABEL).map(([k, v]) => [v, k]));

/** группы столбцов: pr — признаки работы (фильтры и сводки), ra — что влияет на расчёт */
type ColGroups = { pr: boolean; ra: boolean };
const COLS_KEY = 'plot_workcols2';   // прежний ключ хранил три группы (до 2026-09-09)
const GRUPPY: { kod: keyof ColGroups; imya: string; podskazka: string }[] = [
  { kod: 'pr', imya: 'Признаки',
    podskazka: 'Место, контур, этап, рабочее название, единица измерения, работы факта — на расчёт не влияют' },
  { kod: 'ra', imya: 'Расчёт',
    podskazka: 'Раскатка, календарь, логика монтажа, предшественники, корпуса, длительность, тепловой контур' },
];
function readCols(): ColGroups {
  try {
    const st = JSON.parse(localStorage.getItem(COLS_KEY) || '{}') as Partial<ColGroups>;
    return { pr: st.pr !== false, ra: st.ra !== false };
  } catch { return { pr: true, ra: true }; }
}

export function Works({ ctx }: { ctx: ShellCtx }) {
  const [works, setWorks] = useState<Work[]>([]);
  const [spiski, setSpiski] = useState<Spisok[]>([]);
  const [logiki, setLogiki] = useState<Logika[]>([]);
  const [korpusa, setKorpusa] = useState<string[]>([]);
  // работы факта по работам ГПР (2026-09-11): столбец справочника, правка — в карточке
  const [fakty, setFakty] = useState<Record<number, WorkFakt[]>>({});
  const [cols, setCols] = useState<ColGroups>(readCols);
  const [msg, setMsg] = useState('');
  const [pop, setPop] = useState<{ kind: 'kontur' | 'korpusa' | 'global'; w?: Work; at: Anchor } | null>(null);
  const [adding, setAdding] = useState(false);
  // группировка по этапу (#42) убрана владельцем 2026-09-15: работы — одним списком,
  // этап виден в своём столбце и сортируется кликом по заголовку
  // уровни сортировки: первый главный, следующие сортируют внутри него
  const [sort, setSort] = useState<{ i: number; dir: 1 | -1 }[]>([]);
  const can = ctx.canPlan;

  const load = useCallback(async () => {
    const [w, s, l, st, wf] = await Promise.all([
      call<Work[]>('get', p('/api/projects/{pid}/works', { pid: ctx.pid })),
      call<Spisok[]>('get', p('/api/projects/{pid}/spiski', { pid: ctx.pid })),
      call<Logika[]>('get', p('/api/projects/{pid}/logiki', { pid: ctx.pid })),
      call<Record<string, unknown[]>>('get', p('/api/projects/{pid}/structure', { pid: ctx.pid })),
      call<WorkFakt[]>('get', p('/api/projects/{pid}/works-fakt', { pid: ctx.pid })),
    ]);
    if (!w.ok) { setMsg(w.error.message); return; }
    setWorks(w.data);
    if (wf.ok) {
      const po: Record<number, WorkFakt[]> = {};
      for (const x of wf.data) (po[x.work_id] ||= []).push(x);
      setFakty(po);
    }
    if (s.ok) setSpiski(s.data);
    if (l.ok) setLogiki(l.data);
    if (st.ok) setKorpusa(Object.keys(st.data));
    // reloadToken — сигнал оболочки «данные изменились» (например, в карточке работы);
    // без него справочник не видел правок из карточки до перезагрузки (2026-09-11)
  }, [ctx.pid, ctx.reloadToken]);
  useEffect(() => { void load(); }, [load]);

  const opts = (kod: string) => spiski.filter(x => x.kod === kod).map(x => x.znachenie).filter(Boolean);
  const fail = (m: string) => { setMsg(m); ctx.onError?.(m); };
  // оболочка по onChanged сама даст новый reloadToken — второй раз не перечитываем
  const changed = (m = '') => { setMsg(m); if (ctx.onChanged) ctx.onChanged(); else void load(); };
  const setColGroup = (k: keyof ColGroups, v: boolean) => {
    const next = { ...cols, [k]: v };
    setCols(next);
    try { localStorage.setItem(COLS_KEY, JSON.stringify(next)); } catch { /* приватный режим */ }
  };

  // значения столбцов в порядке заголовков — по ним и сортируем
  const SORT_GET: ((w: Work) => string | number)[] = [
    w => (w.name || '').toLowerCase(),
    // признаки
    w => (w.mesto || '').toLowerCase(),
    w => (w.kontur || '').toLowerCase(),
    w => (w.etap || '').toLowerCase(),
    w => (w.rab_name || '').toLowerCase(),
    w => (w.ed_izm || '').toLowerCase(),
    // расчёт
    w => RASK_LABEL[w.raskatka] || w.raskatka || '',
    w => (w.logika || '').toLowerCase(),
    w => (w.preds || []).map(x => x.name).join(', ').toLowerCase(),
    w => (w.korpusa || '').toLowerCase(),
    w => (w.dlit ?? -1),
    w => (w.tk ? 1 : 0),
    // работы факта (12) — в группе «Признаки», по числу активных
    w => (fakty[w.id] || []).filter(x => x.aktivna).length,
    // группа работ (13, #41)
    w => (w.sistema || '').toLowerCase(),
    // календарь (14, #62)
    w => KAL_LABEL[w.kalendar] || w.kalendar || '',
  ];
  const sortClick = (i: number) => setSort(cur => {
    const j = cur.findIndex(x => x.i === i);
    if (j < 0) return [...cur, { i, dir: 1 as const }];        // новый уровень в конец
    if (cur[j].dir === 1) return cur.map((x, k) => (k === j ? { i, dir: -1 as const } : x));
    return cur.filter((_, k) => k !== j);                      // третий клик — снять
  });
  /** стрелка направления и номер уровня, если сортировка не одна */
  const arrow = (i: number) => {
    const j = sort.findIndex(x => x.i === i);
    if (j < 0) return '';
    return (sort[j].dir === 1 ? ' ▲' : ' ▼') + (sort.length > 1 ? String(j + 1) : '');
  };
  /** заголовок столбца: клик — сортировка, третий клик снимает */
  const sh = (i: number, nazvanie: string, gruppa: keyof ColGroups, podskazka?: string) => (
    <th style={hide(gruppa)} className="sortable"
      title={(podskazka ? podskazka + '. ' : '')
        + 'Клик — сортировка по столбцу, второй клик — обратный порядок, третий снимает. '
        + 'Столбцы складываются: сначала первый выбранный, внутри него — следующий (номер у стрелки)'}
      onClick={() => sortClick(i)}>{nazvanie}{arrow(i)}</th>
  );

  const rows = useMemo(() => {
    const keep = works.filter(w => !ctx.matchesWork || ctx.matchesWork(w.name));
    if (!sort.length) return keep;          // исходный порядок — как отдал сервер
    return keep.slice().sort((a, b) => {
      for (const s of sort) {               // уровни по порядку: первый главный
        const g = SORT_GET[s.i];
        const x = g(a), y = g(b);
        if (x < y) return -1 * s.dir;
        if (x > y) return 1 * s.dir;
      }
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [works, ctx, sort]);

  async function patch(w: Work, body: Record<string, unknown>, note?: (d: { sozdano_zadach?: number; udaleno_zadach?: number }) => string) {
    const r = await call<{ sozdano_zadach?: number; udaleno_zadach?: number }>('patch', p('/api/works/{wid}', { wid: w.id }), body);
    if (!r.ok) { fail(r.error.message); void load(); return false; }
    changed(note ? note(r.data) : '');
    return true;
  }
  async function addPred(w: Work, pred: Work) {
    const tid = await ensureBaseTpl(ctx.pid);
    if (!tid) { fail('Не удалось добавить связь'); return; }
    const r = await call('post', p('/api/link-templates/{tid}/rows', { tid }), { work: w.name, pred: pred.name });
    if (!r.ok) { fail(r.error.message); return; }
    changed();
  }
  /** удалить работу (#55, 2026-09-13): сначала подсчёт (?info=1), потом окно с числами,
   *  потом удаление со всем содержимым (?force=1) */
  async function udalitRabotu(w: Work) {
    type Info = { zadach: number; s_faktom: number; svyazej: number; po_korpusam: { korpus: string; zadach: number }[];
                  svyazi: unknown[]; podrabot: number; otmetok_podrabot: number };
    const i = await call<Info>('delete', p('/api/works/{wid}', { wid: w.id }) + '?info=1');
    if (!i.ok) { fail(i.error.message); return; }
    const d = i.data;
    const stroki = [
      'Задач: ' + d.zadach + (d.s_faktom ? ' (с фактом: ' + d.s_faktom + ')' : '')
        + (d.po_korpusam.length ? ' — ' + d.po_korpusam.map(x => x.korpus + ': ' + x.zadach).join(', ') : ''),
      'Связей в шаблонах: ' + d.svyazej,
      d.podrabot ? 'Переделов (работ факта): ' + d.podrabot + (d.otmetok_podrabot ? ', отметок по ним: ' + d.otmetok_podrabot : '') : '',
      '', 'Всё это удалится вместе с работой. Отмене не подлежит.',
    ].filter(x => x !== '');
    const da = await confirmBox('Удалить работу «' + w.name + '»?', { podpis: stroki.join('\n'), ok: 'Удалить', opasno: true });
    if (!da) return;
    const r = await call<{ deleted: number; zadach: number }>('delete', p('/api/works/{wid}', { wid: w.id }) + '?force=1');
    if (!r.ok) { fail(r.error.message); return; }
    changed('Работа «' + w.name + '» удалена' + (r.data.zadach ? ', задач: ' + r.data.zadach : '')
      + '. Нажмите «Пересчитать» в модели, чтобы обновить даты.');
  }
  async function delPred(linkId: number) {
    const r = await call('delete', p('/api/template-rows/{rid}', { rid: linkId }));
    if (!r.ok) { fail(r.error.message); return; }
    changed();
  }

  const hide = (g: keyof ColGroups) => (cols[g] ? undefined : { display: 'none' as const });

  return (
    <div>
      <div className="bar actbar">
        <span className="pv-stolbcy">
          {GRUPPY.map(g => (
            <button key={g.kod} className={'sec pv-btn' + (cols[g.kod] ? ' on' : '')}
              title={g.podskazka + '. Клик — показать или скрыть столбцы'}
              onClick={() => setColGroup(g.kod, !cols[g.kod])}>{g.imya}</button>
          ))}
        </span>
        <span className="grow" />
        {can && <button className="sec icobtn" aria-label="Добавить из глобального справочника"
          title="Добавить работы из глобального справочника (библиотека проектов)"
          onClick={e => setPop({ kind: 'global', at: at(e) })}>📚</button>}
        <span className="pw-muted">{msg}</span>
      </div>
      <div className="tbl"><table>
        <thead>
          <tr>
            <th rowSpan={2} className="sortable"
              title="Клик — сортировка по названию, второй клик — обратный порядок, третий снимает"
              onClick={() => sortClick(0)}>Название{arrow(0)}</th>
            <th colSpan={7} style={hide('pr')} title="Признаки работы: для фильтров, сводок и отчётов; на расчёт не влияют">Признаки</th>
            <th colSpan={7} style={hide('ra')} title="Что определяет создание задач и их сроки в модели">Расчёт</th>
            {/* удаление работы — снова в строке (владелец, #55 2026-09-13): с подтверждением и числом задач */}
            <th rowSpan={2}></th>
          </tr>
          <tr>
            {sh(1, 'Место', 'pr')}
            {sh(2, 'Контур', 'pr', 'Контур — тег для фильтров и сводок (Фасад, Паркинг…). На создание задач не влияет')}
            {sh(3, 'Этап', 'pr')}
            {sh(13, 'Группа работ', 'pr', 'Группа работ (инженерная система и т.п.) — значения ведутся на «Списках», фильтр в модели')}
            {sh(4, 'Рабочее название', 'pr', 'Как работу называют на площадке; показывается в шахматке факта')}
            {sh(5, 'Ед. изм.', 'pr', 'Единица измерения работы — значения ведутся на «Списках»')}
            {sh(12, 'Работы факта', 'pr', 'Переделы работы, по которым отмечается факт; клик по ячейке — открыть их в карточке работы')}
            {sh(6, 'Раскатка', 'ra', 'Как работа превращается в задачи: по типовым этажам · по этажам ниже 0 · по техническим этажам · по этажам с квартирами · по этажам и секциям · на корпус · на объект · вручную')}
            {sh(14, 'Календарь', 'ra', 'Календарь работы: «Календарные дни» — считаются все дни; «Без праздников» — длительность и запас связи не учитывают государственные праздники (список — на «Планирование → Общее»)')}
            {sh(7, 'Логика монтажа', 'ra')}
            {sh(8, 'Предшественники', 'ra')}
            {sh(9, 'Корпуса', 'ra', 'Исключения по корпусам: где ведётся работа (пусто = все)')}
            {sh(10, 'Длит.', 'ra', 'Длительность работы в днях по справочнику')}
            {sh(11, 'ТК', 'ra', 'Тепловой контур: работу нельзя выполнять зимой без теплового контура')}
          </tr>
        </thead>
        <tbody>
          {rows.map(w => (
            <WorkRow key={w.id} w={w} can={can} cols={cols} hide={hide} works={works} opts={opts}
              logNames={logiki.map(l => l.name)} onPatch={patch} onAddPred={addPred} onDelPred={delPred}
              onCard={n => ctx.onWorkCard?.(n)}
              fakty={fakty[w.id] || []} onFakty={() => ctx.onWorkCard?.(w.name, 'fakt')}
              onKontur={e => setPop({ kind: 'kontur', w, at: at(e) })}
              onKorpusa={e => setPop({ kind: 'korpusa', w, at: at(e) })}
              onDelete={udalitRabotu} />
          ))}
          <tr className="grp" style={{ cursor: 'default' }}>
            {/* одна ячейка на всю строку: при скрытых группах столбцов итоги
                больше не разъезжаются (замечание владельца 2026-09-07) */}
            <td colSpan={99}>ИТОГО работ: {rows.length} · задач:{' '}
              {rows.reduce((s, w) => s + (w.zadach || 0), 0)}</td>
          </tr>
          {can && (adding
            ? <AddRow pid={ctx.pid} opts={opts} logNames={logiki.map(l => l.name)} onDone={m => { changed(m); }} onFail={fail} onCancel={() => setAdding(false)} />
            : <tr><td colSpan={99}><a className="tagadd" onClick={() => setAdding(true)} title="Добавить работу">+ работа</a></td></tr>)}
        </tbody>
      </table></div>



      {pop?.kind === 'kontur' && pop.w && <KonturPop w={pop.w} at={pop.at} opts={opts('kontur')} onClose={() => setPop(null)}
        onSave={async vals => { if (await patch(pop.w!, { kontur: vals.join(',') || null })) setPop(null); }} />}
      {pop?.kind === 'korpusa' && pop.w && <KorpusaPop w={pop.w} at={pop.at} korpusa={korpusa} onClose={() => setPop(null)}
        onSave={async vals => {
          const okp = await patch(pop.w!, { korpusa: vals.join(',') }, d => 'Корпуса сохранены: задач создано '
            + (d.sozdano_zadach || 0) + ', удалено пустых ' + (d.udaleno_zadach || 0)
            + '. Нажмите «Пересчитать», чтобы обновить даты.');
          if (okp) setPop(null);
        }} />}
      {pop?.kind === 'global' && <GlobalPop ctx={ctx} at={pop.at} have={works.map(w => w.name.toLowerCase())}
        onClose={() => setPop(null)} onDone={m => changed(m)} onFail={fail} />}
    </div>
  );
}

/** Значение из списка в ячейке: поле с поиском, как поиск работы в панели фильтров
 *  (владелец 2026-09-15; раньше — браузерный select, оформленный тегом, #43/#45).
 *  Значения не из списка тоже видны и остаются выбираемыми */
function TagSelect({ value, label, opts, can, empty, title, onPick }:
  { value: string; label?: string; opts: string[]; can: boolean; empty: boolean; title: string; onPick: (v: string) => void }) {
  const show = label ?? value;
  // значение в ячейке шире поля (93 px) обрезается — «по типовым этажам»,
  // «Типовые снизу вверх», «Благоустройство» видны наполовину. Поэтому в подсказке
  // сначала само значение, потом объяснение колонки (автопрогон читаемости 2026-09-30)
  const podskazka = show ? show + ' — ' + title : title;
  if (!can) return show ? <span className="tag" title={podskazka}>{show}</span> : null;
  const vse = opts.includes(value) || !value ? opts : [value, ...opts];
  return <VyborSpiska value={value} opts={vse} pusto={empty ? '— не задано —' : undefined}
    title={podskazka} onPick={onPick} />;
}

function WorkRow({ w, can, cols, hide, works, opts, logNames, onPatch, onAddPred, onDelPred, onCard, onKontur, onKorpusa,
  fakty, onFakty, onDelete }: {
  w: Work; can: boolean; cols: ColGroups; hide: (g: keyof ColGroups) => { display: 'none' } | undefined;
  works: Work[]; opts: (k: string) => string[]; logNames: string[];
  onPatch: (w: Work, body: Record<string, unknown>) => Promise<boolean>;
  onAddPred: (w: Work, pred: Work) => void; onDelPred: (linkId: number) => void; onCard: (name: string) => void;
  onKontur: (e: React.MouseEvent) => void; onKorpusa: (e: React.MouseEvent) => void;
  fakty: WorkFakt[]; onFakty: () => void; onDelete: (w: Work) => Promise<void>;
}) {
  const [addPred, setAddPred] = useState(false);
  const used = new Set((w.preds || []).map(x => x.name));
  void cols;
  return (
    <tr>
      <td className="pw-link" title="Открыть карточку работы: свойства, связи, подрядчики"
        onClick={() => onCard(w.name)}>{w.name}</td>
      <td style={hide('pr')}><TagSelect value={w.mesto || ''} opts={opts('mesto')} can={can} empty title="место работы (список «Место» в «Списках»)"
        onPick={v => void onPatch(w, { mesto: v || null })} /></td>
      <td style={hide('pr')}><span className="tag" style={{ cursor: can ? 'pointer' : 'default' }}
        title="контур — тег для фильтров и сводок (можно несколько); на создание задач не влияет, это «Раскатка»"
        onClick={e => { if (can) onKontur(e); }}>{w.kontur || '—'}</span></td>
      <td style={hide('pr')}><TagSelect value={w.etap || ''} opts={opts('etap')} can={can} empty title="этап работ («Модель общая»)"
        onPick={v => void onPatch(w, { etap: v || null })} /></td>
      <td style={hide('pr')}><TagSelect value={w.sistema || ''} opts={opts('gruppa')} can={can} empty title="группа работ (список «Группы работ» в «Списках»)"
        onPick={v => void onPatch(w, { uroven2: v || null })} /></td>
      <EditCell value={w.rab_name || ''} can={can} style={hide('pr')}
        onSave={async v => { await onPatch(w, { rabochee_nazvanie: v.trim() || null }); }} />
      <td style={hide('pr')}><TagSelect value={w.ed_izm || ''} opts={opts('ed_izm')} can={can} empty
        title="единица измерения (список «Единицы измерения» в «Списках»)" onPick={v => void onPatch(w, { ed_izm: v || null })} /></td>
      <td style={{ ...hide('pr'), cursor: 'pointer' }} onClick={onFakty}
        title="Работы факта — переделы, по которым отмечается факт. Клик — открыть в карточке работы">
        {fakty.map(x => (
          <span key={x.id} className="tag" style={x.aktivna ? undefined : { opacity: 0.5 }}
            title={x.generalnyj ? 'генеральный передел — по нему окончание задачи'
              : x.aktivna ? undefined : 'скрыта — в итог факта не входит'}>{x.generalnyj ? '★ ' : ''}{x.name}</span>
        ))}
        {!fakty.length && can && <a className="tagadd" title="Добавить работы факта">+</a>}
      </td>
      <td style={hide('ra')}><TagSelect value={RASK_LABEL[w.raskatka] || w.raskatka || ''} opts={Object.values(RASK_LABEL)}
        can={can} empty={false} title="раскатка: как работа превращается в задачи"
        onPick={v => void onPatch(w, { raskatka: RASK_KEY[v] || v })} /></td>
      <td style={hide('ra')}><TagSelect value={KAL_LABEL[w.kalendar] || w.kalendar || ''} opts={Object.values(KAL_LABEL)}
        can={can} empty={false} title="календарь: считать все дни или без государственных праздников"
        onPick={v => void onPatch(w, { kalendar: KAL_KEY[v] || v })} /></td>
      <td style={hide('ra')}><TagSelect value={w.logika || ''} opts={logNames} can={can} empty title="сценарий монтажа"
        onPick={v => void onPatch(w, { logika_montazha: v || null })} /></td>
      <td className="predcell" style={hide('ra')}>
        {(w.preds || []).map(pr => (
          <span key={pr.link_id} className="tag" title={pr.soft ? 'мягкая связь' : undefined}
            style={pr.soft ? { borderStyle: 'dashed', borderColor: '#f79009' } : undefined}>
            {(pr.soft ? '~ ' : '') + pr.name}
            {can && <a className="tagx" title="убрать связь" onClick={() => onDelPred(pr.link_id)}>×</a>}
          </span>
        ))}
        {can && (addPred
          ? <PredVybor opts={works.filter(x => x.id !== w.id && !used.has(x.name)).map(x => x.name)}
              onDone={name => {
                setAddPred(false);
                const pw = works.find(x => x.name === name);
                if (pw) onAddPred(w, pw);
              }} />
          : <a className="tagadd" title="добавить предшественника" onClick={() => setAddPred(true)}>+</a>)}
      </td>
      <td style={hide('ra')}><span className="tag" style={{ cursor: can ? 'pointer' : 'default' }} title="корпуса работы (пусто = все)"
        onClick={e => { if (can) onKorpusa(e); }}>{w.korpusa || 'все'}</span></td>
      <EditCell value={w.dlit != null ? String(w.dlit) : ''} can={can} style={hide('ra')}
        onSave={async v => { await onPatch(w, { dlitelnost: v ? +v.replace(',', '.') : null }); }} />
      <td className="num" style={hide('ra')}>
        <input type="checkbox" checked={!!w.tk} disabled={!can} onChange={e => void onPatch(w, { priznak_tk: e.target.checked })} /></td>
      <td className="num">{can && <button className="del" title="Удалить работу со всеми её задачами (с подтверждением)"
        onClick={() => void onDelete(w)}>×</button>}</td>
    </tr>
  );
}

/** выбор предшественника: поле с поиском сразу в фокусе; ушли без выбора — поле пропадает */
function PredVybor({ opts, onDone }: { opts: string[]; onDone: (name: string) => void }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => { box.current?.querySelector('input')?.focus(); }, []);
  return (
    <div ref={box} style={{ display: 'inline-block', minWidth: 200 }}
      onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) window.setTimeout(() => onDone(''), 0); }}>
      <VyborSpiska value="" opts={opts} placeholder="работа-предшественник…" onPick={onDone} />
    </div>
  );
}

/** текстовая ячейка с правкой по клику; используется и в справочнике «Подрядчики» (2026-09-14) */
export function EditCell({ value, can, style, title, onSave }:
  { value: string; can: boolean; style?: { display: 'none' }; title?: string; onSave: (v: string) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  if (!editing) return <td className={can ? 'edit' : ''} style={style} title={title} onClick={() => { if (can) setEditing(true); }}>{value}</td>;
  const done = async (save: boolean) => { setEditing(false); if (save && v !== value) await onSave(v); else setV(value); };
  return (
    <td style={style}>
      <input autoFocus value={v} style={{ width: '100%' }} onChange={e => setV(e.target.value)} onBlur={() => void done(true)}
        onKeyDown={e => { if (e.key === 'Enter') void done(true); if (e.key === 'Escape') void done(false); }} />
    </td>
  );
}

function AddRow({ pid, opts, logNames, onDone, onFail, onCancel }: {
  pid: number; opts: (k: string) => string[]; logNames: string[];
  onDone: (m: string) => void; onFail: (m: string) => void; onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [rask, setRask] = useState('этажи');
  // все признаки сразу при добавлении (#44, 2026-09-13): пусто — не задано
  const [f, setF] = useState({ mesto: '', kontur: '', etap: '', gruppa: '', logika: '', dlit: '', ed: '' });
  const set = (k: keyof typeof f) => (v: string) => setF(s => ({ ...s, [k]: v }));
  const sel = (k: keyof typeof f, list: string[], title: string) => (
    <VyborSpiska value={f[k]} opts={list} pusto="— не задано —" placeholder={title} title={title}
      style={{ width: 150 }} onPick={set(k)} />
  );
  async function add() {
    const nm = name.trim();
    if (!nm) { onFail('Введите название работы'); return; }
    const body: Record<string, unknown> = { name: nm, raskatka: rask };
    if (f.mesto) body.mesto = f.mesto;
    if (f.kontur) body.kontur = f.kontur;
    if (f.etap) body.etap = f.etap;
    if (f.gruppa) body.uroven2 = f.gruppa;
    if (f.logika) body.logika_montazha = f.logika;
    if (f.ed) body.ed_izm = f.ed;
    if (f.dlit.trim()) { const d = parseFloat(f.dlit.replace(',', '.')); if (isNaN(d)) { onFail('Длительность — число'); return; } body.dlitelnost = d; }
    const r = await call<{ sozdano_zadach: number }>('post', p('/api/projects/{pid}/works', { pid }), body);
    if (!r.ok) { onFail(r.error.message); return; }
    setName('');
    onDone(r.data.sozdano_zadach ? 'Работа добавлена, создано задач: ' + r.data.sozdano_zadach
      + '. Нажмите «Пересчитать», чтобы обновить даты.' : 'Работа добавлена.');
  }
  return (
    <tr>
      {/* одна ячейка на всю строку: раньше строка добавления рисовала свои 13 ячеек
          независимо от скрытых групп столбцов и ломала ширину колонок таблицы
          (замечание владельца 2026-09-08) */}
      <td colSpan={99}>
        <div className="bar" style={{ margin: 0 }}>
          <input autoFocus placeholder="Название новой работы" style={{ width: 320, maxWidth: '40%' }} value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void add(); } if (e.key === 'Escape') onCancel(); }} />
          <VyborSpiska value={rask} opts={Object.keys(RASK_LABEL)} podpisi={RASK_LABEL}
            title="Раскатка — как работа превратится в задачи" style={{ width: 190 }} onPick={setRask} />
          {sel('mesto', opts('mesto'), 'место')}
          {sel('kontur', opts('kontur'), 'контур')}
          {sel('etap', opts('etap'), 'этап')}
          {sel('gruppa', opts('gruppa'), 'группа работ')}
          {sel('logika', logNames, 'логика монтажа')}
          <input style={{ width: 72 }} placeholder="дней" title="Длительность, дней" value={f.dlit} onChange={e => set('dlit')(e.target.value)} />
          {sel('ed', opts('ed_izm'), 'ед. изм.')}
          <button onClick={() => void add()}>Добавить</button>
          <button className="sec" onClick={onCancel}>Отмена</button>
          <span className="pw-muted">Enter — сохранить, Esc — отмена</span>
        </div>
      </td>
    </tr>
  );
}

function KonturPop({ w, at: a, opts, onClose, onSave }: { w: Work; at: Anchor; opts: string[]; onClose: () => void; onSave: (v: string[]) => void }) {
  // значение контура может содержать запятую («Ниже 0,000»), поэтому разбираем
  // строку с оглядкой на список известных значений (2026-09-09)
  const [sel, setSel] = useState<string[]>(kontury(w.kontur || 'этаж', opts));
  const tgl = (o: string) => setSel(s => (s.includes(o) ? s.filter(x => x !== o) : [...s, o]));
  return (
    <Pop at={a} title={'Контуры: ' + w.name} onClose={onClose} width={300}>
      <div style={{ margin: '8px 0' }}>{opts.map(o => (
        <label key={o} style={{ display: 'block', padding: '2px 0' }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={sel.includes(o)} onChange={() => tgl(o)} /> {o}
        </label>))}</div>
      <div className="pw-muted" style={{ margin: '0 0 8px' }}>Контур — тег для фильтров и сводок, можно несколько.
        На создание задач не влияет: как работа превращается в задачи, задаёт колонка «Раскатка».</div>
      <div className="bar" style={{ margin: 0 }}><button onClick={() => onSave(sel)}>Сохранить</button></div>
    </Pop>
  );
}

function KorpusaPop({ w, at: a, korpusa, onClose, onSave }: { w: Work; at: Anchor; korpusa: string[]; onClose: () => void; onSave: (v: string[]) => void }) {
  const [sel, setSel] = useState<string[]>(String(w.korpusa || '').split(',').map(s => s.trim()).filter(Boolean));
  const tgl = (o: string) => setSel(s => (s.includes(o) ? s.filter(x => x !== o) : [...s, o]));
  return (
    <Pop at={a} title={'Корпуса: ' + w.name} onClose={onClose} width={320}>
      <div style={{ margin: '8px 0', columnCount: 2 }}>{korpusa.map(o => (
        <label key={o} style={{ display: 'block', padding: '2px 0' }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={sel.includes(o)} onChange={() => tgl(o)} /> {o}
        </label>))}</div>
      <div className="pw-muted" style={{ margin: '0 0 8px' }}>Ничего не отмечено — работа во всех корпусах.
        После сохранения задачи в новых корпусах досоздаются, а в убранных удаляются только пустые (без ручных дат и факта).</div>
      <div className="bar" style={{ margin: 0 }}>
        <button onClick={() => onSave(sel)}>Сохранить</button>
        <button className="sec" onClick={() => onSave([])}>Все корпуса</button>
      </div>
    </Pop>
  );
}

function GlobalPop({ ctx, at: a, have, onClose, onDone, onFail }:
  { ctx: ShellCtx; at: Anchor; have: string[]; onClose: () => void; onDone: (m: string) => void; onFail: (m: string) => void }) {
  const [list, setList] = useState<Work[] | null>(null);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<number[]>([]);
  const [note, setNote] = useState('');
  useEffect(() => {
    void (async () => {
      const pr = await call<Project[]>('get', '/api/projects');
      if (!pr.ok) { onFail(pr.error.message); return; }
      const gp = pr.data.find(x => x.code === 'global');
      if (!gp) { onFail('Проект «Глобально» не найден.'); onClose(); return; }
      if (gp.id === ctx.pid) { onFail('Это и есть проект «Глобально» — здесь работы заводятся напрямую.'); onClose(); return; }
      const r = await call<Work[]>('get', p('/api/projects/{pid}/works', { pid: gp.id }));
      if (!r.ok) { onFail(r.error.message); return; }
      setList(r.data);
    })();
    // список глобальных работ грузится один раз при открытии окна
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const shown = (list || []).filter(g => !q || g.name.toLowerCase().includes(q.toLowerCase()));
  async function apply() {
    if (!sel.length) { setNote('Отметьте работы галочками.'); return; }
    const r = await call<{ dobavleno: number; sozdano_zadach: number; uzhe_byli: string[] }>(
      'post', p('/api/projects/{pid}/works/from-global', { pid: ctx.pid }), { ids: sel });
    if (!r.ok) { setNote(r.error.message); return; }
    const d = r.data;
    let t = 'Добавлено работ: ' + d.dobavleno + (d.sozdano_zadach ? ', создано задач: ' + d.sozdano_zadach : '')
      + (d.uzhe_byli.length ? ', уже были: ' + d.uzhe_byli.join(', ') : '');
    if (d.sozdano_zadach) t += '. Нажмите «Пересчитать» на экране «Модель».';
    setNote(t); setSel([]);
    onDone(t);
  }
  return (
    <Pop at={a} title="Работы из глобального справочника (проект «Глобально»)" onClose={onClose} width={620}>
      <div className="bar" style={{ margin: '8px 0' }}>
        <input placeholder="Поиск по названию…" style={{ flex: 1 }} value={q} onChange={e => setQ(e.target.value)} />
      </div>
      <div style={{ maxHeight: '46vh', overflow: 'auto', border: '1px solid var(--plot-line)', borderRadius: 8, padding: '6px 10px' }}>
        {list === null ? 'Загрузка…' : shown.length === 0 ? 'Ничего не найдено.' : (() => {
          // группы по этапу (#39, 2026-09-13): заголовок этапа, под ним работы без приставки
          const poEtapu = new Map<string, Work[]>();
          shown.forEach(g => { const k = g.etap || 'Без этапа'; poEtapu.set(k, [...(poEtapu.get(k) || []), g]); });
          const etapy = [...poEtapu.keys()].sort((a, b) => (a === 'Без этапа' ? 1 : b === 'Без этапа' ? -1 : a.localeCompare(b, 'ru', { numeric: true })));
          return etapy.map(et => {
            const gr = poEtapu.get(et)!;
            const svobodnye = gr.filter(g => !have.includes(g.name.toLowerCase())).map(g => g.id);
            const vseVybrany = svobodnye.length > 0 && svobodnye.every(id => sel.includes(id));
            return (
              <div key={et} style={{ margin: '6px 0' }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontWeight: 600, margin: '2px 0' }}
                  title="Отметить все работы этапа">
                  <input type="checkbox" style={{ width: 'auto' }} checked={vseVybrany} disabled={!svobodnye.length}
                    onChange={e => setSel(s => (e.target.checked
                      ? [...new Set([...s, ...svobodnye])] : s.filter(x => !svobodnye.includes(x))))} />
                  {et}
                </label>
                {gr.map(g => {
                  const has = have.includes(g.name.toLowerCase());
                  return (
                    <label key={g.id} style={{ display: 'block', padding: '2px 0 2px 20px', color: has ? '#98a2b3' : undefined }}>
                      <input type="checkbox" style={{ width: 'auto' }} disabled={has} checked={sel.includes(g.id)}
                        onChange={e => setSel(s => (e.target.checked ? [...s, g.id] : s.filter(x => x !== g.id)))} />
                      {' ' + g.name + (has ? ' (уже в проекте)' : '')}
                    </label>
                  );
                })}
              </div>
            );
          });
        })()}
      </div>
      <div className="bar" style={{ marginBottom: 0 }}>
        <button onClick={() => void apply()}>Добавить выбранные</button>
        <span className="pw-muted">{note}</span>
      </div>
    </Pop>
  );
}
